import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ScannerDemoDrawer
 * Cajón lateral (Offcanvas) que aísla de manera ordenada todas las herramientas
 * de simulación, pruebas automáticas (HU-04 / HU-05), pruebas acústicas del sintetizador
 * y corte de red perimetral offline, evitando la saturación del operador en campo.
 */
export const ScannerDemoDrawer = ({
    isOpen,
    onClose,
    demoBarTab,
    setDemoBarTab,
    scanningInProgress,
    manualInput,
    // HU-05 handlers
    onHu05XoloShopSale,
    onHu05TacosSale,
    onHu05CalienteActivation,
    onHu05NissanActivation,
    onHu05RedeemGoldenCoupon,
    onHu05DoubleRedeemWarning,
    // HU-04 handlers
    onHu04ValidGeneral,
    onHu04AntiPassback,
    onHu04RestrictedVip,
    onHu04ValidVip,
    onHu04ConcurrentAntiPassback,
    onDemoResetTicket,
    // Network & Audio handlers
    isSimulatedOffline,
    offlineQueueLength = 0,
    onToggleSimulateOffline,
    onSyncOfflineQueue,
    syncingOfflineQueue,
    onClearOfflineQueue,
    onPlayGrantedTone,
    onPlayWarningTone,
    onPlayDeniedTone
}) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 1060,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: '100%',
                        maxWidth: '560px',
                        height: '100%',
                        backgroundColor: '#121216',
                        borderLeft: '2px solid #f59e0b',
                        boxShadow: '-10px 0 35px rgba(0, 0, 0, 0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header del Drawer */}
                    <div
                        style={{
                            padding: '18px 20px',
                            background: 'linear-gradient(180deg, #1c1608 0%, #121216 100%)',
                            borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 10
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: '#f59e0b',
                                    color: '#09090b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    fontWeight: 900
                                }}
                            >
                                🛠️
                            </div>
                            <div>
                                <h3
                                    style={{
                                        margin: 0,
                                        fontSize: '1.1rem',
                                        fontWeight: 900,
                                        color: '#ffffff'
                                    }}
                                >
                                    Panel de Pruebas y Simulación
                                </h3>
                                <span style={{ fontSize: '0.75rem', color: '#fde68a' }}>
                                    Herramientas de evaluación automática (HU-04 / HU-05)
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'rgba(255, 255, 255, 0.08)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: '#ffffff',
                                borderRadius: 10,
                                width: 34,
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: 16,
                                fontWeight: 700
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Selector de Pestañas de Pruebas */}
                    <div
                        style={{
                            padding: '12px 18px',
                            background: '#18181b',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                            display: 'flex',
                            gap: 8
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setDemoBarTab('HU05')}
                            style={{
                                flex: 1,
                                padding: '9px 12px',
                                borderRadius: 10,
                                border: demoBarTab === 'HU05' ? '1.5px solid #f59e0b' : '1px solid rgba(255,255,255,0.1)',
                                background: demoBarTab === 'HU05' ? 'rgba(245, 158, 11, 0.22)' : 'rgba(255,255,255,0.04)',
                                color: demoBarTab === 'HU05' ? '#fde68a' : '#a1a1aa',
                                fontSize: '0.8rem',
                                fontWeight: 800,
                                cursor: 'pointer'
                            }}
                        >
                            🍔 HU-05: POS, Marcas y Canje
                        </button>

                        <button
                            type="button"
                            onClick={() => setDemoBarTab('HU04')}
                            style={{
                                flex: 1,
                                padding: '9px 12px',
                                borderRadius: 10,
                                border: demoBarTab === 'HU04' ? '1.5px solid #10b981' : '1px solid rgba(255,255,255,0.1)',
                                background: demoBarTab === 'HU04' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.04)',
                                color: demoBarTab === 'HU04' ? '#6ee7b7' : '#a1a1aa',
                                fontSize: '0.8rem',
                                fontWeight: 800,
                                cursor: 'pointer'
                            }}
                        >
                            🚪 HU-04: Puertas & Aforo
                        </button>
                    </div>

                    {/* Contenido con Scroll de los Escenarios */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '18px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16
                        }}
                    >
                        {/* Escenarios HU-05 */}
                        {demoBarTab === 'HU05' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div
                                    style={{
                                        fontSize: '0.74rem',
                                        fontWeight: 800,
                                        color: '#f59e0b',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.8
                                    }}
                                >
                                    Escenarios de Prueba HU-05 (1 Toque)
                                </div>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05XoloShopSale}
                                    style={{
                                        background: 'rgba(245, 158, 11, 0.14)',
                                        border: '1.5px solid #f59e0b',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🛍️ 1. Venta Rápida XoloShop ($730: Gorra + Bufanda → XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fde68a', marginTop: 4 }}>
                                        Suma $730 MXN en STAND_XOLOSHOP y estampa sello POS
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05TacosSale}
                                    style={{
                                        background: 'rgba(245, 158, 11, 0.14)',
                                        border: '1.5px solid #fbbf24',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🌮 2. Consumo Tacos & Grill ($270: Tacos + Cerveza → XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fde68a', marginTop: 4 }}>
                                        Suma $270 MXN en STAND_TACOS y actualiza gasto acumulado
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05CalienteActivation}
                                    style={{
                                        background: 'rgba(220, 38, 38, 0.16)',
                                        border: '1.5px solid #ef4444',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ⚽ 3. Activación Caliente.mx (3 Goles → XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fca5a5', marginTop: 4 }}>
                                        Sella patrocinador obligatorio (MANDATORY_ACTIVATION)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05NissanActivation}
                                    style={{
                                        background: 'rgba(220, 38, 38, 0.14)',
                                        border: '1.5px solid #f87171',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🚗 4. Activación Reto Nissan (Test Drive → XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fecaca', marginTop: 4 }}>
                                        Registra participación en ACT_NISSAN (100 pts)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05RedeemGoldenCoupon}
                                    style={{
                                        background: 'rgba(5, 150, 105, 0.18)',
                                        border: '1.5px solid #10b981',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🎁 5. Canjear Cupón Dorado Físico de XOLO-0001
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#6ee7b7', marginTop: 4 }}>
                                        Pantalla Verde + Arpegio confirmando entrega del Vaso Oficial
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu05DoubleRedeemWarning}
                                    style={{
                                        background: 'rgba(217, 119, 6, 0.18)',
                                        border: '1.5px solid #f59e0b',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ⚠️ 6. Intentar Doble Canje del mismo Cupón (Alerta Amarilla)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fde68a', marginTop: 4 }}>
                                        Bloquea segunda entrega (ALREADY_REDEEMED + Pulso 440Hz)
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Escenarios HU-04 */}
                        {demoBarTab === 'HU04' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div
                                    style={{
                                        fontSize: '0.74rem',
                                        fontWeight: 800,
                                        color: '#10b981',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.8
                                    }}
                                >
                                    Escenarios de Prueba HU-04 (1 Toque)
                                </div>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu04ValidGeneral}
                                    style={{
                                        background: 'rgba(5, 150, 105, 0.18)',
                                        border: '1.5px solid #10b981',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ✅ 1. Ingreso Válido GENERAL en Puerta 1 (XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#6ee7b7', marginTop: 4 }}>
                                        Pantalla Verde + Arpegio Ascendente (C5→E5→G5→C6)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu04AntiPassback}
                                    style={{
                                        background: 'rgba(217, 119, 6, 0.18)',
                                        border: '1.5px solid #f59e0b',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ⚠️ 2. Doble Entrada / Anti-Passback en Puerta 1 (XOLO-0001)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fde68a', marginTop: 4 }}>
                                        Pantalla Amarilla + Doble Pulso 440 Hz (Hora y Operador)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu04RestrictedVip}
                                    style={{
                                        background: 'rgba(220, 38, 38, 0.18)',
                                        border: '1.5px solid #ef4444',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ⛔ 3. Boleto GENERAL intentando entrar a Puerta VIP
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#fca5a5', marginTop: 4 }}>
                                        Pantalla Roja + Buzzer Grave (180Hz→90Hz)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu04ValidVip}
                                    style={{
                                        background: 'rgba(16, 185, 129, 0.16)',
                                        border: '1.5px solid #34d399',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🟢 4. Boleto VIP Válido en Puerta VIP (XOLO-0071)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#a7f3d0', marginTop: 4 }}>
                                        Acceso Hospitalidad VIP + Reingreso Permitido
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={onHu04ConcurrentAntiPassback}
                                    style={{
                                        background: 'rgba(139, 92, 246, 0.18)',
                                        border: '1.5px solid #a78bfa',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        ⚡ 5. Prueba Anti-Passback Simultáneo (50 ms)
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#ddd6fe', marginTop: 4 }}>
                                        Promise.all XOLO-0002: Puerta A (GRANTED) vs Puerta B (DENIED)
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={scanningInProgress}
                                    onClick={() => onDemoResetTicket(manualInput || 'XOLO-0001')}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.06)',
                                        border: '1.5px solid rgba(255, 255, 255, 0.25)',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        textAlign: 'left',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                        🔄 6. Reiniciar Historial de Boleto Demo ({manualInput || 'XOLO-0001'})
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: '#d4d4d8', marginTop: 4 }}>
                                        Limpia asistencia, compras, sellos y recompensas
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Bloque: Resiliencia Offline y Red Perimetral */}
                        <div
                            style={{
                                background: '#18181b',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 14,
                                padding: 14
                            }}
                        >
                            <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#fde68a', marginBottom: 8 }}>
                                📶 Resiliencia Perimetral Offline (HU-07)
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={onToggleSimulateOffline}
                                    style={{
                                        flex: 1,
                                        minWidth: 160,
                                        background: isSimulatedOffline ? '#dc2626' : 'rgba(245, 158, 11, 0.2)',
                                        color: '#ffffff',
                                        border: isSimulatedOffline ? '1.5px solid #fca5a5' : '1.5px solid #f59e0b',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        fontSize: '0.78rem',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isSimulatedOffline
                                        ? `📶 Cortada (${offlineQueueLength} en cola)`
                                        : '📶 Simular Corte de Red'}
                                </button>

                                {offlineQueueLength > 0 && (
                                    <>
                                        <button
                                            type="button"
                                            disabled={syncingOfflineQueue}
                                            onClick={onSyncOfflineQueue}
                                            style={{
                                                background: '#10b981',
                                                color: '#09090b',
                                                border: 'none',
                                                borderRadius: 8,
                                                padding: '8px 12px',
                                                fontSize: '0.78rem',
                                                fontWeight: 900,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {syncingOfflineQueue ? '⏳...' : `🔄 Sincronizar (${offlineQueueLength})`}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onClearOfflineQueue}
                                            style={{
                                                background: 'rgba(255,255,255,0.08)',
                                                color: '#fca5a5',
                                                border: '1px solid rgba(220,38,38,0.4)',
                                                borderRadius: 8,
                                                padding: '8px 10px',
                                                fontSize: '0.78rem',
                                                fontWeight: 700,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🗑️ Vaciar
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Bloque: Test Acústico Web Audio API */}
                        <div
                            style={{
                                background: '#18181b',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 14,
                                padding: 14
                            }}
                        >
                            <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#a1a1aa', marginBottom: 8 }}>
                                🎹 Test Acústico Web Audio API (Cero archivos externos):
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={onPlayGrantedTone}
                                    style={{
                                        background: '#059669',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '6px 10px',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    🔊 Arpegio Éxito
                                </button>
                                <button
                                    type="button"
                                    onClick={onPlayWarningTone}
                                    style={{
                                        background: '#d97706',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '6px 10px',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    🔊 Doble Pulso
                                </button>
                                <button
                                    type="button"
                                    onClick={onPlayDeniedTone}
                                    style={{
                                        background: '#dc2626',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '6px 10px',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    🔊 Buzzer Grave
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ScannerDemoDrawer;
